<template>
    <div>
        <app-layout>
            <template v-slot:header>
                <Header :title="$t('prep_student_document')"
                        :isNewButton="false"
                        :is-filter="false">
                </Header>
            </template>
            <template v-slot:header-mobile>
                <HeaderMobile :title="$t('prep_student_document')"
                              :isNewButton="false"
                              :is-filter="false">
                </HeaderMobile>
            </template>

            <div class="student-dashboard-container p-4">
                <!-- Welcome & Action Header Card -->
                <div class="dashboard-welcome-card mb-4 d-flex justify-content-between align-items-center flex-wrap p-4">
                    <div>
                        <h4 class="welcome-title mb-1 text-primary font-weight-bold">
                            <i class="ri-file-user-line mr-2"></i>{{ $t('prep_student_document_title') }}
                        </h4>
                        <p class="welcome-desc text-muted mb-0">{{ $t('prep_student_document_desc') }}</p>
                    </div>
                    <b-button v-if="checkPermission('prepstudentdocument_store')"
                              variant="primary"
                              class="new-doc-btn px-4 py-2 font-weight-bold mt-2 mt-md-0"
                              @click="createFormShow">
                        <i class="ri-add-line mr-1"></i> {{ $t('upload_new_document') }}
                    </b-button>
                </div>

                <!-- Document List Card -->
                <div class="document-list-section">
                    <h5 class="section-title mb-3 font-weight-bold">
                        <i class="ri-list-check-2 mr-1"></i> {{ $t('application_history') }}
                    </h5>

                    <!-- Loading State -->
                    <div v-if="datatable.isLoading" class="text-center py-5 card loading-card">
                        <b-spinner variant="primary" :label="$t('loading')" class="mb-3"></b-spinner>
                        <p class="text-muted">{{ $t('loading_documents_wait') }}</p>
                    </div>

                    <!-- Empty State -->
                    <div v-else-if="datatable.rows.length === 0" class="text-center py-5 card empty-card p-5">
                        <div class="empty-icon-wrapper mb-3">
                            <i class="ri-file-warning-line text-muted"></i>
                        </div>
                        <h5 class="empty-title mb-2">{{ $t('no_registered_documents') }}</h5>
                        <p class="text-muted mb-4">{{ $t('no_registered_documents_desc') }}</p>
                        <b-button v-if="checkPermission('prepstudentdocument_store')"
                                  variant="outline-primary"
                                  class="px-4 py-2 font-weight-bold"
                                  @click="createFormShow">
                            {{ $t('upload_first_document') }}
                        </b-button>
                    </div>

                    <!-- Card Grid View -->
                    <div v-else class="document-grid">
                        <b-row>
                            <b-col cols="12" md="6" lg="4" v-for="row in datatable.rows" :key="row.id" class="mb-4">
                                <div class="document-item-card h-100">
                                    <div class="card-status-indicator" :class="getStatusDetails(row.status).variant"></div>
                                    <div class="card-body p-4 d-flex flex-column h-100 justify-content-between">
                                        <div>
                                            <!-- Top Row: Type & Status Badge -->
                                            <div class="d-flex justify-content-between align-items-center mb-3">
                                                <div class="d-flex align-items-center text-truncate mr-2">
                                                    <div class="doc-icon-container mr-2 flex-shrink-0">
                                                        <i class="ri-file-text-line"></i>
                                                    </div>
                                                    <h5 class="document-name mb-0 font-weight-bold text-dark text-truncate" :title="getLocaleText(row, 'document_type_name')">
                                                        {{ getLocaleText(row, 'document_type_name') }}
                                                    </h5>
                                                </div>
                                                <span class="status-badge flex-shrink-0" :class="getStatusDetails(row.status).variant">
                                                    <i :class="getStatusDetails(row.status).icon + ' mr-1'"></i>
                                                    {{ getLocaleText(row, 'status_name') }}
                                                </span>
                                            </div>

                                            <!-- Date Information -->
                                            <div class="date-info-box p-3 mb-3">
                                                <div class="d-flex align-items-center mb-2">
                                                    <i class="ri-calendar-event-line text-muted mr-2"></i>
                                                    <span class="date-label text-muted mr-2">{{ $t('excuse_start_date') }}:</span>
                                                    <span class="date-value font-weight-bold">{{ row.start_date ? toLocaleDate(row.start_date) : '-' }}</span>
                                                </div>
                                                <div class="d-flex align-items-center">
                                                    <i class="ri-calendar-check-line text-muted mr-2"></i>
                                                    <span class="date-label text-muted mr-2">{{ $t('excuse_end_date') }}:</span>
                                                    <span class="date-value font-weight-bold">{{ row.end_date ? toLocaleDate(row.end_date) : '-' }}</span>
                                                </div>
                                            </div>

                                            <!-- File Attachment Info -->
                                            <div v-if="row.file" class="file-info-box p-2 mb-3 d-flex align-items-center">
                                                <i class="ri-file-pdf-line text-danger mr-2" v-if="row.file.mime_type === 'application/pdf'"></i>
                                                <i class="ri-image-line text-success mr-2" v-else-if="row.file.mime_type && row.file.mime_type.includes('image')"></i>
                                                <i class="ri-file-line text-secondary mr-2" v-else></i>
                                                <span class="file-name-text text-truncate small font-weight-bold" :title="row.file.file_name">
                                                    {{ row.file.file_name }}
                                                </span>
                                            </div>
                                        </div>

                                        <!-- Attachment if any -->
                                        <div class="card-footer-action pt-3 border-top d-flex align-items-center justify-content-between">
                                            <span v-if="row.file" class="file-attachment text-primary small">
                                                <i class="ri-attachment-line mr-1"></i> {{ $t('document_attached') }}
                                            </span>
                                            <b-button v-if="checkPermission('prepstudentdocument_delete') && isDeletable(row.status)"
                                                      variant="outline-danger"
                                                      size="sm"
                                                      class="delete-doc-btn font-weight-bold"
                                                      @click="formDelete(row.id)">
                                                <i class="ri-delete-bin-line mr-1"></i> {{ $t('delete') }}
                                            </b-button>
                                        </div>
                                    </div>
                                </div>
                            </b-col>
                        </b-row>
                    </div>
                </div>
            </div>

            <!-- Create Modal -->
            <CommonModal ref="createFormModal" size="lg" :onHideOnlyX="true" @bHideModalHeaderClose="formClear" v-if="checkPermission('prepstudentdocument_store')">
                <template v-slot:CommonModalTitle>
                    {{ $t('new_document_submission') }}
                </template>
                <template v-slot:CommonModalContent>
                    <create-form @createFormSuccess="createFormSuccess"
                                 v-if="formProcess=='create'"
                    />
                </template>
            </CommonModal>
        </app-layout>
    </div>
</template>

<script>
import AppLayout from "@/layouts/AppLayout"
import Header from "@/layouts/AppLayout/Header"
import HeaderMobile from "@/layouts/AppLayout/HeaderMobile"
import qs from 'qs'
import PrepStudentDocumentService from "@/services/PrepStudentDocumentService";
import CommonModal from "@/components/elements/CommonModal";
import {ValidationObserver, ValidationProvider} from "vee-validate";
import CreateForm from "@/modules/prepStudentDocument/pages/student/CreateForm.vue";

export default {
    components: {
        AppLayout,
        Header,
        HeaderMobile,
        CommonModal,
        CreateForm,
        ValidationProvider,
        ValidationObserver,
    },
    metaInfo() {
        return {
            title: this.$t('prep_student_document')
        }
    },
    data() {
        return {
            id: 0,
            form: {},
            formProcess: null,
            datatable: {
                isLoading: false,
                rows: [],
                queryParams: {
                    filter: {},
                    sort: 'id',
                    page: 1,
                    limit: 100
                }
            }
        }
    },
    methods: {
        filter() {
            this.datatable.queryParams.page = 1;
            this.getRows();
        },
        getRows() {
            this.datatable.isLoading = true;
            const config = {
                params: {
                    ...this.datatable.queryParams
                },
                paramsSerializer: (params) => qs.stringify(params, {encode: false})
            };

            return PrepStudentDocumentService.getAll(config)
                .then((response) => {
                    this.datatable.rows = response.data.data
                })
                .finally(() => {
                    this.datatable.isLoading = false;
                });
        },
        formClear() {
            this.formId = null
            this.formProcess = null
        },
        async createFormSuccess() {
            this.$refs.createFormModal.$refs.commonModal.hide()
            this.getRows()
        },
        createFormShow() {
            this.formId = null
            this.form = {};
            this.formProcess = 'create'
            this.$refs.createFormModal.$refs.commonModal.show()
        },
        formDelete(id) {
            const row = this.datatable.rows.find(r => r.id === id);
            if (row && !this.isDeletable(row.status)) {
                return;
            }
            this.$swal
                .fire({
                    text: this.$t('are_you_sure_to_delete'),
                    showCancelButton: true,
                    confirmButtonText: this.$t('yes'),
                    cancelButtonText: this.$t('no')
                })
                .then((result) => {
                    if (result.isConfirmed) {
                        PrepStudentDocumentService.del(id)
                            .then(response => {
                                this.$toast.success(this.$t('api.' + response.data.message));
                                this.getRows();
                                this.formClear();
                            })
                            .catch(error => {
                                this.$toast.error(this.$t('api.' + error.data.message));
                            });
                    }
                })
        },
        isDeletable(status) {
            if (!status) return true;
            const lower = status.toLowerCase();
            return lower !== 'approved' && lower !== 'rejected';
        },
        getStatusDetails(status) {
            if (!status) return { text: this.$t('pending'), variant: 'warning', icon: 'ri-time-line' };
            const lower = status.toLowerCase();
            if (lower === 'approved' ) {
                return { text: this.$t('approved'), variant: 'success', icon: 'ri-checkbox-circle-line' };
            }
            if (lower === 'rejected') {
                return { text: this.$t('declined'), variant: 'danger', icon: 'ri-close-circle-line' };
            }
            return { text: this.$t('pending'), variant: 'warning', icon: 'ri-time-line' };
        }
    },
    created() {
        this.filter();
    }
};
</script>

<style scoped>
.student-dashboard-container {
    background-color: #f8f9fa;
    min-height: 100vh;
    font-family: 'Inter', sans-serif;
}

.dashboard-welcome-card {
    background: linear-gradient(135deg, #ffffff 0%, #f1f4f8 100%);
    border-radius: 16px;
    border: 1px solid rgba(0, 0, 0, 0.05);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.03);
}

.welcome-title {
    font-size: 1.4rem;
    letter-spacing: -0.5px;
}

.new-doc-btn {
    border-radius: 10px;
    font-size: 0.95rem;
    box-shadow: 0 4px 15px rgba(0, 123, 255, 0.2);
    transition: all 0.25s ease;
}

.new-doc-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 123, 255, 0.3);
}

/* Stats Cards */
.stat-card {
    background-color: #ffffff;
    border-radius: 14px;
    padding: 20px;
    display: flex;
    align-items: center;
    border: 1px solid rgba(0, 0, 0, 0.04);
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.02);
    transition: all 0.25s ease;
}

.stat-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.06);
}

.stat-icon-wrapper {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 1.4rem;
    margin-right: 15px;
}

.stat-details {
    display: flex;
    flex-direction: column;
}

.stat-label {
    font-size: 0.85rem;
    color: #6c757d;
    font-weight: 500;
}

.stat-value {
    font-size: 1.6rem;
    font-weight: 700;
    margin: 0;
    color: #212529;
}

/* Stat card themes */
.stat-total .stat-icon-wrapper { background-color: rgba(0, 123, 255, 0.1); color: #007bff; }
.stat-total { border-left: 4px solid #007bff; }

.stat-pending .stat-icon-wrapper { background-color: rgba(255, 193, 7, 0.1); color: #ffc107; }
.stat-pending { border-left: 4px solid #ffc107; }

.stat-approved .stat-icon-wrapper { background-color: rgba(40, 167, 69, 0.1); color: #28a745; }
.stat-approved { border-left: 4px solid #28a745; }

.stat-declined .stat-icon-wrapper { background-color: rgba(220, 53, 69, 0.1); color: #dc3545; }
.stat-declined { border-left: 4px solid #dc3545; }

/* Document Cards */
.document-item-card {
    background-color: #ffffff;
    border-radius: 14px;
    border: 1px solid rgba(0, 0, 0, 0.06);
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.02);
    position: relative;
    overflow: hidden;
    transition: all 0.3s ease;
}

.document-item-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.08);
}

.card-status-indicator {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
}

.card-status-indicator.success { background-color: #28a745; }
.card-status-indicator.warning { background-color: #ffc107; }
.card-status-indicator.danger { background-color: #dc3545; }

.doc-icon-container {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background-color: #f1f3f5;
    color: #495057;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 1.25rem;
}

.status-badge {
    padding: 6px 12px;
    border-radius: 20px;
    font-size: 0.78rem;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
}

.status-badge.success { background-color: rgba(40, 167, 69, 0.1); color: #28a745; }
.status-badge.warning { background-color: rgba(255, 193, 7, 0.15); color: #b78a00; }
.status-badge.danger { background-color: rgba(220, 53, 69, 0.1); color: #dc3545; }

.document-name {
    font-size: 1.1rem;
    line-height: 1.4;
    letter-spacing: -0.2px;
}

.date-info-box {
    background-color: #f8f9fa;
    border-radius: 10px;
    border: 1px solid rgba(0, 0, 0, 0.02);
}

.file-info-box {
    background-color: #f1f3f5;
    border-radius: 10px;
    border: 1px solid rgba(0, 0, 0, 0.05);
}

.date-label {
    font-size: 0.8rem;
    width: 70px;
}

.date-value {
    font-size: 0.85rem;
    color: #343a40;
}

/* Loading & Empty Cards */
.loading-card, .empty-card {
    border-radius: 16px;
    border: 1px solid rgba(0, 0, 0, 0.05);
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.02);
    background-color: #ffffff;
}

.empty-icon-wrapper {
    font-size: 3.5rem;
    opacity: 0.6;
}

.empty-title {
    font-weight: 700;
    color: #343a40;
}

.section-title {
    color: #495057;
    font-size: 1.05rem;
    letter-spacing: 0.2px;
}

.delete-doc-btn {
    border-radius: 8px;
    font-size: 0.82rem;
    transition: all 0.2s ease;
}
</style>

